import { useState, useEffect } from 'react';

import styles from './IconRingEditor.module.css';

/**
 * IconRingEditor Component
 *
 * Specialized editor for icon-ring page type (e.g., Contacts, Courses pages)
 * Allows managing a list of icon links with label, icon, and URL
 *
 * Features:
 * - Add/edit/remove icon items
 * - Reorder items with up/down buttons
 * - Form validation (required fields, URL format)
 * - Save/Cancel functionality
 * - Unsaved changes tracking
 */

/**
 *
 */
type IconItem = {
  readonly label: string;
  readonly icon: string;
  readonly url: string;
  readonly order: number;
};

/**
 *
 */
type IconRingEditorProps = {
  readonly slug: string;
  readonly pageTitle: string;
  readonly initialContent: Record<string, unknown>;
  readonly onSave: (content: Record<string, unknown>) => Promise<void>;
  readonly isSaving: boolean;
};

const IconRingEditor = ({
  pageTitle,
  initialContent,
  onSave,
  isSaving,
}: IconRingEditorProps): JSX.Element => {
  const [items, setItems] = useState<ReadonlyArray<IconItem>>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  // Initialize form with initial content
  useEffect(() => {
    const initialItems = (initialContent.items as ReadonlyArray<IconItem>) || [];
    setItems(initialItems);
    setIsDirty(false);
    setErrors({});
  }, [initialContent]);

  // Add new item
  const addItem = (): void => {
    const newItem: IconItem = {
      label: '',
      icon: '',
      url: '',
      order: items.length + 1,
    };
    setItems([...items, newItem]);
    setIsDirty(true);
  };

  // Update item field
  const updateItem = (index: number, field: keyof IconItem, value: string): void => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    setItems(updatedItems);
    setIsDirty(true);

    // Clear error for this field
    if (errors[index]?.[field]) {
      const newErrors = { ...errors };
      delete newErrors[index][field];
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setErrors(newErrors);
    }
  };

  // Remove item
  const removeItem = (index: number): void => {
    const confirmed = window.confirm('Are you sure you want to remove this link?');
    if (!confirmed) return;

    const updatedItems = items.filter((_, i) => i !== index);
    // Recalculate order
    const reorderedItems = updatedItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));
    setItems(reorderedItems);
    setIsDirty(true);

    // Remove errors for this item
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  // Move item up
  const moveItemUp = (index: number): void => {
    if (index === 0) return;

    const updatedItems = [...items];
    [updatedItems[index - 1], updatedItems[index]] = [updatedItems[index], updatedItems[index - 1]];
    // Recalculate order
    const reorderedItems = updatedItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));
    setItems(reorderedItems);
    setIsDirty(true);
  };

  // Move item down
  const moveItemDown = (index: number): void => {
    if (index === items.length - 1) return;

    const updatedItems = [...items];
    [updatedItems[index], updatedItems[index + 1]] = [updatedItems[index + 1], updatedItems[index]];
    // Recalculate order
    const reorderedItems = updatedItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));
    setItems(reorderedItems);
    setIsDirty(true);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    items.forEach((item, index) => {
      const itemErrors: Record<string, string> = {};

      // Validate label
      if (!item.label.trim()) {
        itemErrors.label = 'Label is required';
        isValid = false;
      } else if (item.label.length > 50) {
        itemErrors.label = 'Label must be 50 characters or less';
        isValid = false;
      }

      // Validate icon
      if (!item.icon.trim()) {
        itemErrors.icon = 'Icon is required';
        isValid = false;
      } else if (item.icon.length > 20) {
        itemErrors.icon = 'Icon must be 20 characters or less';
        isValid = false;
      }

      // Validate URL
      if (!item.url.trim()) {
        itemErrors.url = 'URL is required';
        isValid = false;
      } else if (
        !item.url.startsWith('http://') &&
        !item.url.startsWith('https://') &&
        !item.url.startsWith('mailto:') &&
        !item.url.startsWith('tel:')
      ) {
        itemErrors.url = 'URL must start with http://, https://, mailto:, or tel:';
        isValid = false;
      }

      if (Object.keys(itemErrors).length > 0) {
        newErrors[index] = itemErrors;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle save
  const handleSave = async (): Promise<void> => {
    // Validate form
    if (!validateForm()) {
      alert('Please fix all errors before saving');
      return;
    }

    // Recalculate order before saving
    const itemsToSave = items.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    try {
      await onSave({
        items: itemsToSave,
      });
      setIsDirty(false);
      setErrors({});
    } catch (err) {
      // Error handled by parent component
      console.error('Save failed:', err);
    }
  };

  // Handle cancel
  const handleCancel = (): void => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      );
      if (!confirmed) return;
    }

    // Reset to initial values
    const initialItems = (initialContent.items as ReadonlyArray<IconItem>) || [];
    setItems(initialItems);
    setIsDirty(false);
    setErrors({});
  };

  return (
    <div className={styles.iconRingEditor}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Edit: {pageTitle}</h1>
          <p className={styles.subtitle}>Icon Ring Content</p>
        </div>
        <button onClick={addItem} className={styles.addButton} type="button">
          + Add Link
        </button>
      </div>

      {/* Items List */}
      <div className={styles.itemsList}>
        {items.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No icon links yet. Click "Add Link" to create one.</p>
          </div>
        ) : (
          items.map((item, index) => (
            <div key={index} className={styles.itemCard}>
              {/* Order and Move Buttons */}
              <div className={styles.itemOrder}>
                <span className={styles.orderNumber}>{index + 1}</span>
                <div className={styles.moveButtons}>
                  <button
                    onClick={() => moveItemUp(index)}
                    disabled={index === 0}
                    className={styles.moveButton}
                    title="Move up"
                    type="button"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveItemDown(index)}
                    disabled={index === items.length - 1}
                    className={styles.moveButton}
                    title="Move down"
                    type="button"
                  >
                    ▼
                  </button>
                </div>
              </div>

              {/* Item Fields */}
              <div className={styles.itemFields}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor={`label-${index}`}>
                    Label
                  </label>
                  <input
                    id={`label-${index}`}
                    type="text"
                    className={`${styles.input} ${errors[index]?.label ? styles.inputError : ''}`}
                    value={item.label}
                    onChange={(e) => updateItem(index, 'label', e.target.value)}
                    placeholder="e.g., Facebook, Email, Course 1"
                    maxLength={50}
                  />
                  {errors[index]?.label ? (
                    <span className={styles.error}>{errors[index].label}</span>
                  ) : null}
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor={`icon-${index}`}>
                    Icon
                  </label>
                  <input
                    id={`icon-${index}`}
                    type="text"
                    className={`${styles.input} ${errors[index]?.icon ? styles.inputError : ''}`}
                    value={item.icon}
                    onChange={(e) => updateItem(index, 'icon', e.target.value)}
                    placeholder="e.g., 📧 or facebook"
                    maxLength={20}
                  />
                  {errors[index]?.icon ? (
                    <span className={styles.error}>{errors[index].icon}</span>
                  ) : null}
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor={`url-${index}`}>
                    URL
                  </label>
                  <input
                    id={`url-${index}`}
                    type="text"
                    className={`${styles.input} ${errors[index]?.url ? styles.inputError : ''}`}
                    value={item.url}
                    onChange={(e) => updateItem(index, 'url', e.target.value)}
                    placeholder="https://example.com or mailto:email@example.com"
                  />
                  {errors[index]?.url ? (
                    <span className={styles.error}>{errors[index].url}</span>
                  ) : null}
                </div>
              </div>

              {/* Remove Button */}
              <button
                onClick={() => removeItem(index)}
                className={styles.removeButton}
                title="Remove link"
                type="button"
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <button
          onClick={handleCancel}
          className={styles.cancelButton}
          disabled={isSaving}
          type="button"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={styles.saveButton}
          disabled={isSaving || !isDirty || items.length === 0}
          type="button"
        >
          {isSaving ? (
            <>
              <span className={styles.spinner}></span>
              Saving...
            </>
          ) : (
            '💾 Save Changes'
          )}
        </button>
      </div>

      {/* Unsaved Changes Warning */}
      {isDirty && !isSaving ? (
        <div className={styles.unsavedWarning}>⚠️ You have unsaved changes</div>
      ) : null}
    </div>
  );
};

export default IconRingEditor;
